import React, { useState } from "react";
import { Book, Search, Loader2, Volume2, AlertCircle, GraduationCap, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTheme } from "@/theme";

export default function DictionaryPanel({ initialWord = "" }) {
  const [word, setWord] = useState(initialWord);
  const [results, setResults] = useState<any>(null);
  const [scholarlyResults, setScholarlyResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("general");
  const { isDark } = useTheme();

  // Theme-aware styles
  const textPrimary = "text-white";
  const textSecondary = isDark ? "text-slate-300" : "text-gray-700";
  const textMuted = isDark ? "text-slate-400" : "text-gray-600";
  const borderColor = isDark ? "border-white/5" : "border-slate-300";

  const lookupWord = async (searchWord?: string) => {
    const wordToSearch = searchWord || word;
    if (!wordToSearch.trim()) return;

    setLoading(true);
    setError(null);
    setResults(null);
    setScholarlyResults(null);

    try {
      // General dictionary lookup
      const response = await fetch(
        `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(wordToSearch.trim())}`
      );

      if (response.ok) {
        const data = await response.json();
        setResults(data[0]);
      }

      // Scholarly/academic definition lookup (placeholder)
      if (activeTab === "scholarly") {
        setScholarlyResults({
          term: wordToSearch,
          academicDefinition: "Scholarly definitions require backend AI integration. Please contact the developer to enable this feature.",
          etymology: "",
          academicUsage: "",
          examples: [],
          relatedTerms: [],
          fieldOfStudy: "",
          notableReferences: []
        });
      }

      if (!response.ok && activeTab === "general") {
        if (response.status === 404) {
          setError("No results found. Try a simpler form of the word.");
        } else {
          setError("Failed to look up word. Please try again.");
        }
      }
    } catch (err) {
      setError("Failed to connect. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const playPronunciation = (audioUrl: string) => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    lookupWord();
  };

  return (
    <div className="h-full flex flex-col text-white">
      <div className={`p-4 border-b ${borderColor}`}>
        <div className="flex items-center gap-2 mb-2">
          <Book className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold text-white">Dictionary</h3>
        </div>
        <p className={`text-xs ${textMuted} mb-3`}>
          Look up words while you write
        </p>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-3">
          <TabsList className="grid w-full grid-cols-2 bg-white/5">
            <TabsTrigger value="general" className="text-xs data-[state=active]:bg-purple-500/30">
              <Globe className="w-3 h-3 mr-1" />
              General
            </TabsTrigger>
            <TabsTrigger value="scholarly" className="text-xs data-[state=active]:bg-purple-500/30">
              <GraduationCap className="w-3 h-3 mr-1" />
              Scholarly
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            value={word}
            onChange={(e) => setWord(e.target.value)}
            placeholder="Look up a word..."
            className="bg-white/5 border-white/10 text-white text-sm"
          />
          <Button
            type="submit"
            size="sm"
            disabled={loading || !word.trim()}
            className="btn-gradient text-white"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
          </Button>
        </form>
      </div>

      <ScrollArea className="flex-1 p-4">
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {/* General Dictionary Results */}
        {activeTab === "general" && results && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-white">{results.word}</h2>
                {results.phonetics?.find((p: any) => p.audio) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-blue-400 hover:text-blue-300"
                    onClick={() => playPronunciation(results.phonetics.find((p: any) => p.audio)?.audio)}
                  >
                    <Volume2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {results.phonetic && (
                <p className={`text-sm ${textMuted} italic`}>{results.phonetic}</p>
              )}
            </div>

            {results.meanings?.map((meaning: any, idx: number) => (
              <div key={idx} className="space-y-2">
                <Badge className="bg-purple-500/20 text-purple-300 border-0">
                  {meaning.partOfSpeech}
                </Badge>

                <div className="space-y-2 pl-2 border-l-2 border-white/10">
                  {meaning.definitions?.slice(0, 4).map((def: any, defIdx: number) => (
                    <div key={defIdx}>
                      <p className="text-sm text-white">
                        {defIdx + 1}. {def.definition}
                      </p>
                      {def.example && (
                        <p className="text-xs text-slate-400 italic mt-1 pl-3">
                          "{def.example}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {meaning.synonyms?.length > 0 && (
                  <div className="mt-2">
                    <span className="text-xs text-slate-400">Synonyms: </span>
                    <span className="text-xs text-blue-400">
                      {meaning.synonyms.slice(0, 5).join(", ")}
                    </span>
                  </div>
                )}

                {meaning.antonyms?.length > 0 && (
                  <div>
                    <span className="text-xs text-slate-400">Antonyms: </span>
                    <span className="text-xs text-orange-400">
                      {meaning.antonyms.slice(0, 5).join(", ")}
                    </span>
                  </div>
                )}
              </div>
            ))}

            {results.sourceUrls?.length > 0 && (
              <div className="pt-2 border-t border-white/10">
                <a
                  href={results.sourceUrls[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-400 hover:underline"
                >
                  View source →
                </a>
              </div>
            )}
          </div>
        )}

        {/* Scholarly Dictionary Results */}
        {activeTab === "scholarly" && scholarlyResults && (
          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-bold text-white">{scholarlyResults.term || word}</h2>
              {scholarlyResults.fieldOfStudy && (
                <Badge className="mt-1 bg-emerald-500/20 text-emerald-300 border-0">
                  {scholarlyResults.fieldOfStudy}
                </Badge>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <h4 className="text-xs font-semibold text-purple-400 uppercase tracking-wide mb-1">Academic Definition</h4>
                <p className="text-sm text-white leading-relaxed">{scholarlyResults.academicDefinition}</p>
              </div>
              {/* The rest of the scholarly fields will be empty unless you add backend AI */}
            </div>
          </div>
        )}

        {!results && !scholarlyResults && !error && !loading && (
          <div className="text-center py-8">
            <Book className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-400">
              {activeTab === "scholarly"
                ? "Enter a term to see its scholarly definition, etymology, and academic usage."
                : "Enter a word to see its definition, pronunciation, and examples."}
            </p>
          </div>
        )}
      </ScrollArea>
    </div>
  );
}